import { Box, Flex, Link, useDisclosure } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useCustomerGeoLocationStore } from '../../../../bff-api/stores/useCustomerGeoLocationStore';
import { useScrollListener } from '../../../../hooks/useScrollListener';
import { PageUrl } from '../../../../pages/utils/page-url';
import { useSLTConfig } from '../../../../slt/config/SLTConfigProvider';
import { Text } from '../../../../ui-components';
import { Popover } from '../../../../ui-components/popover';
import { rawUrlHash, useCustomPreferenceOrDefault, useUrl } from '../../../../utils/api';
import { CustomPreference } from '../../../../utils/api/CustomPreference';
import { ConditionalRender } from '../../../conditionally-render/ConditionalRender';
import { ChefIcon, EventsIcon, StoreIcon, WhiskIcon } from '../../../icons';
import { Store } from '../../../store';

export const MenuStore: React.FC = () => {
    const { absUrlMap } = useSLTConfig();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const { data } = useCustomerGeoLocationStore();
    const { isNotScrolled } = useScrollListener();

    const urlHash = rawUrlHash({ action: PageUrl.SEARCH_SHOW, args: ['cgid', 'in-store-classes'] });
    const classUrl = absUrlMap[urlHash];
    const enableRioSEO = useCustomPreferenceOrDefault(CustomPreference.ENABLE_RIO_SEO, true);
    const storesFindUrl = useUrl(PageUrl.STORES_FIND);

    const storesUrl = enableRioSEO ? PageUrl.STORES : storesFindUrl;

    return data?.name ? (
        <Popover
            id="header.menu.store"
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            triggerElement={
                <Flex data-testid="header.menu.store" alignItems="center" cursor="pointer" gap={1}>
                    <StoreIcon data-testid="header.menu.store.icon" margin={1} />

                    <ConditionalRender visibility={{ base: false, md: isNotScrolled }}>
                        <Text
                            id="header.menu.store.label"
                            className="border-b-2 border-slt-pepper-1000"
                        >
                            {data.name}
                        </Text>
                    </ConditionalRender>
                </Flex>
            }
            headerElement={
                <Text id="header.menu.store.logged_in.name" size="large-2" style="bold">
                    <FormattedMessage id="nearest_store" defaultMessage="Nearest Store" />
                </Text>
            }
            bodyElement={
                <>
                    <Box paddingX={4}>
                        <Store id="header-menu" store={data} />
                    </Box>

                    {data.c_participateInCulinary ? (
                        <Link
                            data-testid="header.menu.store.view_class.link"
                            href={classUrl}
                            paddingX={4}
                            _hover={{ textDecoration: 'none' }}
                        >
                            <Flex
                                alignItems="center"
                                justifyContent="center"
                                borderRadius={4}
                                borderWidth={1}
                                borderColor="slt-pepper.1000"
                                paddingX={8}
                                paddingY={2}
                            >
                                <Text style="bold">
                                    <FormattedMessage
                                        id="view.cooking.class"
                                        defaultMessage="View Cooking Class"
                                    />
                                </Text>
                            </Flex>
                        </Link>
                    ) : null}

                    <Box height={4} background="slt-beige.400" />

                    <Flex direction="column" gap={4} paddingX={4}>
                        <Flex data-testid="header.menu.store.cooking.classes" gap={2}>
                            <Flex
                                width={14}
                                height={14}
                                alignItems="center"
                                justifyContent="center"
                                borderRadius={80}
                                background="slt-beige.400"
                                shrink={0}
                            >
                                <WhiskIcon data-testid="header.menu.store.cooking.classes.icon" />
                            </Flex>

                            <Flex direction="column">
                                <Text id="header.menu.store.cooking.classes.title">
                                    <FormattedMessage
                                        id="cooking.classes"
                                        defaultMessage="Cooking Classes"
                                    />
                                </Text>

                                <Text id="header.menu.store.cooking.classes.subtitle" size="small">
                                    <FormattedMessage
                                        id="hands.on.cooking.experiences"
                                        defaultMessage="Hands-on cooking experiences led by resident chefs"
                                    />
                                </Text>
                            </Flex>
                        </Flex>

                        <Flex data-testid="header.menu.store.events" gap={2}>
                            <Flex
                                width={14}
                                height={14}
                                alignItems="center"
                                justifyContent="center"
                                borderRadius={80}
                                background="slt-beige.400"
                                shrink={0}
                            >
                                <EventsIcon
                                    data-testid="header.menu.store.events.icon"
                                    width={6}
                                    height={6}
                                    margin={1}
                                />
                            </Flex>

                            <Flex direction="column">
                                <Text id="header.menu.store.events.title">
                                    <FormattedMessage id="events" defaultMessage="Events" />
                                </Text>

                                <Text id="header.menu.store.events.subtitle" size="small">
                                    <FormattedMessage
                                        id="learn.about.brands.try.new.products.and.more"
                                        defaultMessage="Learn about brands, try new products and more"
                                    />
                                </Text>
                            </Flex>
                        </Flex>

                        <Flex data-testid="header.menu.store.ask.the.chef" gap={2}>
                            <Flex
                                width={14}
                                height={14}
                                alignItems="center"
                                justifyContent="center"
                                borderRadius={80}
                                background="slt-beige.400"
                                shrink={0}
                            >
                                <ChefIcon
                                    data-testid="header.menu.store.ask.the.chef.icon"
                                    width={6}
                                    height={6}
                                    margin={1}
                                />
                            </Flex>

                            <Flex direction="column">
                                <Text id="header.menu.store.ask.the.chef.title">
                                    <FormattedMessage
                                        id="ask.the.chef"
                                        defaultMessage="Ask the Chef"
                                    />
                                </Text>

                                <Text id="header.menu.store.ask.the.chef.subtitle" size="small">
                                    <FormattedMessage
                                        id="get.personalized.tips.from.chefs"
                                        defaultMessage="Get personalized tips from our resident chefs to elevate your cooking."
                                    />
                                </Text>
                            </Flex>
                        </Flex>
                    </Flex>

                    <Box height={4} background="slt-beige.400" />

                    <Link
                        data-testid="header.menu.store.find_a_store.link"
                        href={storesUrl}
                        paddingX={4}
                        _hover={{ textDecoration: 'none' }}
                    >
                        <Text style="bold" className="border-b-2 border-slt-pepper-300">
                            <FormattedMessage id="find.a.store" defaultMessage="Find a Store" />
                        </Text>
                    </Link>
                </>
            }
        />
    ) : (
        <Link data-testid="header.menu.stores" href={storesUrl} _hover={{ textDecoration: 'none' }}>
            <Flex alignItems="center" gap={1} cursor="pointer">
                <StoreIcon data-testid="header.menu.stores.icon" margin={1} />

                <ConditionalRender visibility={{ base: false, md: isNotScrolled }}>
                    <Text
                        id="header.menu.stores.label"
                        className="border-b-2 border-slt-pepper-1000"
                    >
                        <FormattedMessage id="header.stores" defaultMessage="Stores" />
                    </Text>
                </ConditionalRender>
            </Flex>
        </Link>
    );
};
